import * as signalR from '@microsoft/signalr';
import type { RealTimeMetricsDto } from '../types/dashboard';
import {
  getSignalRErrorMessage,
  logSignalR,
  logSignalRError,
  logSignalRWarn,
} from '../utils/signalRDiagnostics';

export interface MetricsSignalREvents {
  onRealTimeMetricsUpdated?: (data: RealTimeMetricsDto) => void;
}

/**
 * SignalR service for the MetricsHub (WebSockets only — no Long Polling).
 */
class MetricsSignalRService {
  private connection: signalR.HubConnection | null = null;
  private isConnecting = false;
  private events: MetricsSignalREvents = {};
  private _onReconnecting: (() => void) | null = null;
  private _onReconnected: (() => void) | null = null;
  private _onClosed: ((error?: Error) => void) | null = null;
  private lastErrorMessage: string | null = null;
  /** Retained for subscribe/unsubscribe/reconnect — Azure SignalR hub invokes do not keep negotiate query params. */
  private tenantId: string | null = null;
  private projectId: string | null = null;

  /**
   * Connects to the metrics hub and subscribes to updates.
   *
   * Tenant/project are passed as hub method arguments (required with Azure SignalR).
   * Query params on the hub URL remain for negotiate-time middleware only.
   */
  async connect(baseUrl: string, token: string, tenantId: string, projectId?: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    this.tenantId = tenantId;
    this.projectId = projectId ?? null;

    const url = new URL(`${baseUrl}/hubs/metrics`);
    url.searchParams.set('tenantId', tenantId);
    if (projectId) {
      url.searchParams.set('projectId', projectId);
    }
    const hubUrl = url.toString();

    try {
      // Tear down a previous non-connected instance before rebuilding.
      if (this.connection) {
        try {
          await this.connection.stop();
        } catch {
          // Ignore stop failures during rebuild.
        }
        this.connection = null;
      }

      logSignalR('metrics', 'Connecting', {
        hubUrl,
        transport: 'WebSockets',
        tenantId,
        projectId: projectId ?? null,
      });

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => token,
          transport: signalR.HttpTransportType.WebSockets,
          skipNegotiation: false,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            const delay = Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
            logSignalRWarn('metrics', 'Scheduling reconnect', retryContext.retryReason, {
              previousRetryCount: retryContext.previousRetryCount,
              elapsedMs: retryContext.elapsedMilliseconds,
              nextDelayMs: delay,
            });
            return delay;
          },
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // The server pings every 2.5 min (KeepAliveInterval in Program.cs); the SignalR default of
      // 30s would declare the connection dead between pings.
      this.connection.serverTimeoutInMilliseconds = 6 * 60 * 1000;

      this.setupEventHandlers();
      this.setupLifecycleHandlers();

      await this.connection.start();
      this.isConnecting = false;
      this.lastErrorMessage = null;

      logSignalR('metrics', 'Connected', {
        hubUrl,
        transport: 'WebSockets',
        connectionId: this.connection.connectionId,
        state: this.connection.state,
      });

      await this.subscribeToMetrics();
    } catch (error) {
      this.isConnecting = false;
      this.lastErrorMessage = getSignalRErrorMessage(error, 'Failed to connect to metrics hub');
      logSignalRError('metrics', 'Connect failed', error, {
        hubUrl,
        transport: 'WebSockets',
        tenantId,
        projectId: projectId ?? null,
        hint: 'WebSockets-only. With Azure SignalR, negotiate stays on the API and WS should go to *.service.signalr.net. Check Azure:SignalR:ConnectionString, SignalR App Server RBAC on the API MI, CORS, and auth token.',
      });
      throw error;
    }
  }

  /**
   * Disconnects from the metrics hub.
   */
  async disconnect(): Promise<void> {
    if (!this.connection) return;
    try {
      if (this.connection.state === signalR.HubConnectionState.Connected && this.tenantId) {
        await this.connection.invoke('UnsubscribeFromMetrics', this.tenantId, this.projectId);
      }
    } catch (error) {
      logSignalRWarn('metrics', 'Unsubscribe failed during disconnect', error);
    }

    logSignalR('metrics', 'Disconnecting', { connectionId: this.connection.connectionId });
    await this.connection.stop();
    this.connection = null;
    this.events = {};
    this.lastErrorMessage = null;
    this.tenantId = null;
    this.projectId = null;
  }

  /**
   * Registers event handlers.
   */
  on(events: MetricsSignalREvents): void {
    this.events = { ...this.events, ...events };
  }

  /**
   * Unregisters all event handlers.
   */
  off(): void {
    this.events = {};
  }

  getState(): signalR.HubConnectionState | null {
    return this.connection?.state ?? null;
  }

  getLastErrorMessage(): string | null {
    return this.lastErrorMessage;
  }

  /**
   * Sets callbacks for connection state transitions.
   */
  setReconnectionCallbacks(
    onReconnecting: (() => void) | null,
    onReconnected: (() => void) | null,
    onClosed?: ((error?: Error) => void) | null,
  ): void {
    this._onReconnecting = onReconnecting;
    this._onReconnected = onReconnected;
    this._onClosed = onClosed ?? null;
  }

  private async subscribeToMetrics(): Promise<void> {
    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    if (!this.tenantId) {
      const error = new Error('Tenant id is required to subscribe to metrics');
      this.lastErrorMessage = error.message;
      logSignalRError('metrics', 'SubscribeToMetrics failed', error, {
        connectionId: this.connection.connectionId,
      });
      throw error;
    }

    try {
      await this.connection.invoke('SubscribeToMetrics', this.tenantId, this.projectId);
      logSignalR('metrics', 'Subscribed to metrics group', {
        connectionId: this.connection.connectionId,
        tenantId: this.tenantId,
        projectId: this.projectId,
      });
    } catch (error) {
      this.lastErrorMessage = getSignalRErrorMessage(error, 'Failed to subscribe to metrics');
      logSignalRError('metrics', 'SubscribeToMetrics failed', error, {
        connectionId: this.connection.connectionId,
        tenantId: this.tenantId,
        projectId: this.projectId,
      });
      throw error;
    }
  }

  private setupLifecycleHandlers(): void {
    if (!this.connection) return;

    this.connection.onclose((error) => {
      this.isConnecting = false;
      if (error) {
        this.lastErrorMessage = getSignalRErrorMessage(error, 'Metrics hub connection closed');
        logSignalRError('metrics', 'Connection closed with error', error, {
          connectionId: this.connection?.connectionId,
        });
      } else {
        logSignalR('metrics', 'Connection closed', {
          connectionId: this.connection?.connectionId,
        });
      }
      this._onClosed?.(error);
    });

    this.connection.onreconnecting((error) => {
      this.lastErrorMessage = getSignalRErrorMessage(error, 'Reconnecting to metrics hub…');
      logSignalRWarn('metrics', 'Reconnecting', error, {
        connectionId: this.connection?.connectionId,
        state: this.connection?.state,
      });
      this._onReconnecting?.();
    });

    this.connection.onreconnected(async (connectionId) => {
      logSignalR('metrics', 'Reconnected', {
        connectionId,
        transport: 'WebSockets',
        state: this.connection?.state,
      });
      this.lastErrorMessage = null;
      try {
        // Group membership is lost on reconnect — must re-subscribe.
        await this.subscribeToMetrics();
      } catch {
        // subscribeToMetrics already logged
      }
      this._onReconnected?.();
    });
  }

  private setupEventHandlers(): void {
    if (!this.connection) return;

    this.connection.on('RealTimeMetricsUpdated', (data: RealTimeMetricsDto) => {
      this.events.onRealTimeMetricsUpdated?.(data);
    });
  }
}

export const metricsSignalRService = new MetricsSignalRService();
